import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Gamepad2, Plus, Edit, RefreshCw, Calendar, Clock, MapPin, Tv, X, Activity, Timer, ExternalLink, Bot, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Copy, Info, MicStage, Upload } from '../ui/icons';
import {
  ColDef,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowEditingStoppedEvent,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import type { CustomCellEditorProps } from 'ag-grid-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import ResponseDisplay from '../ui/ResponseDisplay';
import DataTable from '../../utils/DataTable';
import { useDataTableRowEditing } from '../../utils/useDataTableRowEditing';
import { gamesApi, imageMatchingApi, seasonApi, teamApi, venueBotMappingApi, ApiResponse, Game, GameUpdate, GameResponse, RecentRequest, TeamRecord, VenueBotMapping } from '../../services/api';
import { ACTIVE_SEASON_OPTIONS } from '../../data/seasonData';
import { formatTime } from '../../utils/formatTimeZone';
import { isKnownVenue } from '../../utils/venueOptions';
import Colors, { withAlpha } from '../../constants/Colors';
import CopyButton from '../ui/CopyButton';
import GameImportModal from './GameImportModal';

// Mirrors the backend's normalize_venue_key() so a venue typed with different
// casing/whitespace still matches its bot ID mapping.
function normalizeVenueKey(venue: string): string {
  return venue.trim().toLowerCase().split(/\s+/).filter(Boolean).join(' ');
}

// Fields the Games Record grid requires before a row can be saved or published.
const REQUIRED_GRID_FIELDS: { field: keyof Game; label: string }[] = [
  { field: 'date', label: 'Match Date' },
  { field: 'time', label: 'Time' },
  { field: 'venue', label: 'Venue' },
  { field: 'bot_id', label: 'Bot ID' },
];

// Audio URLs are optional per side, so only a non-empty value is checked: it must be an
// http(s) URL with a dotted host and no whitespace.
const AUDIO_URL_REGEX = /^https?:\/\/[^\s/?#]+\.[^\s/?#]+(?:[/?#]\S*)?$/i;
const INVALID_AUDIO_URL_MESSAGE = 'Enter a valid URL starting with http:// or https://';

function isInvalidAudioUrl(value?: string | null): boolean {
  const trimmed = (value ?? '').trim();
  return !!trimmed && !AUDIO_URL_REGEX.test(trimmed);
}

// Stable module-level reference for the Games Record grid's alwaysPassFilter (see usage).
const gridAlwaysPassFilter = () => true;

function getMissingGridFields(game: Game): Set<string> {
  const missing = new Set<string>();
  REQUIRED_GRID_FIELDS.forEach(({ field }) => {
    if (!String(game[field] ?? '').trim()) missing.add(field);
  });
  return missing;
}

function TeamLogoBadge({
  logoUrl,
  teamName,
  themeColor,
  size = 72,
}: {
  logoUrl?: string;
  teamName: string;
  themeColor?: string;
  size?: number;
}) {
  const [hasError, setHasError] = useState(false);

  if (!logoUrl || hasError) {
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.35))',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: '70%',
            height: '70%',
            borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              color: '#FFFFFF',
              fontSize: `${Math.round(size * 0.11)}px`,
              fontWeight: 800,
              letterSpacing: '0.03em',
              lineHeight: 1.15,
              textAlign: 'center',
            }}
          >
            HOME
            <br />
            STREAM
          </span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={`${teamName} logo`}
      style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.35))' }}
      onError={() => setHasError(true)}
    />
  );
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'unscheduled', label: 'Unpublished' },
  { value: 'scheduled', label: 'Published' },
  { value: 'revoked', label: 'Revoked' },
];

function DateCellEditor(props: CustomCellEditorProps<Game, string>) {
  return (
    <input
      type="date"
      autoFocus
      value={props.value ?? ''}
      min={getTodayEasternDateKey()}
      onChange={(e) => props.onValueChange(e.target.value)}
      style={{ width: '100%', height: '100%', border: 'none', outline: 'none', padding: '0 8px' }}
    />
  );
}

function TimeCellEditor(props: CustomCellEditorProps<Game, string>) {
  return (
    <input
      type="time"
      autoFocus
      value={props.value ?? ''}
      onChange={(e) => props.onValueChange(e.target.value)}
      style={{ width: '100%', height: '100%', border: 'none', outline: 'none', padding: '0 8px' }}
    />
  );
}

function toDateInputValue(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function parseDateInputValue(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function getNthSundayUTC(year: number, month: number, n: number): number {
  const firstOfMonth = new Date(Date.UTC(year, month - 1, 1));
  const firstWeekday = firstOfMonth.getUTCDay();
  const firstSunday = firstWeekday === 0 ? 1 : 8 - firstWeekday;
  return firstSunday + (n - 1) * 7;
}

// US Eastern DST: starts 2:00 AM EST (07:00 UTC) on the 2nd Sunday of March,
// ends 2:00 AM EDT (06:00 UTC) on the 1st Sunday of November. Comparing the
// exact UTC instant (not just the calendar date) avoids mislabeling the hours
// right around the transition.
function getEasternUTCOffsetHoursForInstant(utcMs: number): number {
  const year = new Date(utcMs).getUTCFullYear();
  const dstStartDay = getNthSundayUTC(year, 3, 2);
  const dstEndDay = getNthSundayUTC(year, 11, 1);
  const dstStart = Date.UTC(year, 2, dstStartDay, 7, 0, 0);
  const dstEnd = Date.UTC(year, 10, dstEndDay, 6, 0, 0);
  return utcMs >= dstStart && utcMs < dstEnd ? 4 : 5;
}

// For a wall-clock Eastern date/time (used when converting EST -> UTC), the
// transition happens at 2:00 AM local time on the same Sundays.
function getEasternUTCOffsetHoursForLocalDateTime(year: number, month: number, day: number, hour: number): number {
  const dstStartDay = getNthSundayUTC(year, 3, 2);
  const dstEndDay = getNthSundayUTC(year, 11, 1);
  const current = Date.UTC(year, month - 1, day, hour);
  const dstStart = Date.UTC(year, 2, dstStartDay, 2);
  const dstEnd = Date.UTC(year, 10, dstEndDay, 2);
  return current >= dstStart && current < dstEnd ? 4 : 5;
}

function convertESTDateStringToUTC(dateStr: string, timeStr?: string): string {
  if (!timeStr) return dateStr;

  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;

  const [hoursStr, minutesStr] = timeStr.split(':');
  const estHour = parseInt(hoursStr, 10);
  const estMinute = parseInt(minutesStr, 10);
  if (Number.isNaN(estHour) || Number.isNaN(estMinute)) return dateStr;

  const offsetHours = getEasternUTCOffsetHoursForLocalDateTime(year, month, day, estHour);
  const utcInstant = new Date(Date.UTC(year, month - 1, day, estHour, estMinute) + offsetHours * 60 * 60 * 1000);
  const yyyy = utcInstant.getUTCFullYear();
  const mm = String(utcInstant.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(utcInstant.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function convertUTCDateStringToEST(dateStr: string, timeStr?: string): string {
  if (!timeStr) return dateStr;

  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;

  const [hoursStr, minutesStr] = timeStr.split(':');
  const utcHour = parseInt(hoursStr, 10);
  const utcMinute = parseInt(minutesStr, 10);
  if (Number.isNaN(utcHour) || Number.isNaN(utcMinute)) return dateStr;

  const utcInstantMs = Date.UTC(year, month - 1, day, utcHour, utcMinute);
  const offsetHours = getEasternUTCOffsetHoursForInstant(utcInstantMs);
  const estInstant = new Date(utcInstantMs - offsetHours * 60 * 60 * 1000);

  const yyyy = estInstant.getUTCFullYear();
  const mm = String(estInstant.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(estInstant.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function convertESTTimeStringToUTC(value: string, dateStr?: string): string {
  const [hoursStr, minutesStr] = value.split(':');
  const estHour = parseInt(hoursStr, 10);
  if (Number.isNaN(estHour)) return value;

  let offsetHours = 5;
  if (dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (year && month && day) {
      offsetHours = getEasternUTCOffsetHoursForLocalDateTime(year, month, day, estHour);
    }
  }
  const utcHour = (estHour + offsetHours) % 24;
  return `${String(utcHour).padStart(2, '0')}:${minutesStr}`;
}

function convertUTCTimeStringToEST(value: string, dateStr?: string): string {
  if (!value) return "";
  const [hoursStr, minutesStr] = value.split(':');
  const utcHour = parseInt(hoursStr, 10);
  const utcMinute = parseInt(minutesStr, 10);
  if (Number.isNaN(utcHour) || Number.isNaN(utcMinute)) return value;

  if (!dateStr) {
    const estHour = (utcHour - 5 + 24) % 24;
    return `${String(estHour).padStart(2, '0')}:${minutesStr}`;
  }

  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) {
    const estHour = (utcHour - 5 + 24) % 24;
    return `${String(estHour).padStart(2, '0')}:${minutesStr}`;
  }

  const utcInstantMs = Date.UTC(year, month - 1, day, utcHour, utcMinute);
  const offsetHours = getEasternUTCOffsetHoursForInstant(utcInstantMs);
  const estInstant = new Date(utcInstantMs - offsetHours * 60 * 60 * 1000);

  const estHour = estInstant.getUTCHours();
  return `${String(estHour).padStart(2, '0')}:${minutesStr}`;
}

function toLocalDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Game dates are scheduled and displayed in Eastern time, so "today" for past-date checks
// must also be Eastern "today" - not the admin's browser-local date. Using the browser's
// local date here caused false "past date" positives (which silently block saving the whole
// row, any field) for any admin outside the US Eastern timezone whenever the two "today"s
// disagree (e.g. it's already tomorrow locally while still today in Eastern time).
function getTodayEasternDateKey(): string {
  const now = Date.now();
  const offsetHours = getEasternUTCOffsetHoursForInstant(now);
  const eastern = new Date(now - offsetHours * 60 * 60 * 1000);
  return `${eastern.getUTCFullYear()}-${String(eastern.getUTCMonth() + 1).padStart(2, '0')}-${String(eastern.getUTCDate()).padStart(2, '0')}`;
}

function isPastDate(date: Date): boolean {
  return toLocalDateKey(date) < getTodayEasternDateKey();
}

function isPastDateKey(dateKey: string): boolean {
  return Boolean(dateKey) && dateKey < getTodayEasternDateKey();
}

function normalizeToastError(message: string): string {
  return message.replace(/\s+/g, ' ').trim();
}


type ThemedDatePickerProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
  required?: boolean;
};

function ThemedDatePicker({ label, value, onChange, compact = false, required = false }: ThemedDatePickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = parseDateInputValue(value);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => selectedDate ?? new Date());

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate) setViewMonth(selectedDate);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const monthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  const calendarStart = new Date(monthStart);
  calendarStart.setDate(monthStart.getDate() - monthStart.getDay());

  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(calendarStart);
    day.setDate(calendarStart.getDate() + index);
    return day;
  });

  const monthLabel = monthStart.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className={compact ? 'form-group games-date-filter' : 'analytics-filter analytics-date-filter'} ref={containerRef}>
      <span className={compact ? 'games-date-label' : 'analytics-filter-label'}>
        {label}
        {required && <span style={{ color: Colors.error }}> *</span>}
      </span>
      <button
        type="button"
        className={compact ? 'games-date-trigger' : 'analytics-date-trigger'}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={`Select ${label.toLowerCase()} date`}
      >
        <span>{value || 'Select date'}</span>
        <Calendar size={14} />
      </button>

      {isOpen && (
        <div className="analytics-date-popover">
          <div className="analytics-date-header">
            <button
              type="button"
              className="analytics-date-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <div className="analytics-date-title">{monthLabel}</div>
            <button
              type="button"
              className="analytics-date-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="analytics-date-grid analytics-date-grid-head">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>

          <div className="analytics-date-grid">
            {days.map((day) => {
              const inMonth = day >= monthStart && day <= monthEnd;
              const isSelected =
                !!selectedDate &&
                day.getFullYear() === selectedDate.getFullYear() &&
                day.getMonth() === selectedDate.getMonth() &&
                day.getDate() === selectedDate.getDate();
              const disabled = isPastDate(day);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={`analytics-date-day ${inMonth ? '' : 'muted'} ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (disabled) return;
                    onChange(toDateInputValue(day));
                    setIsOpen(false);
                  }}
                  disabled={disabled}
                  aria-disabled={disabled}
                  title={disabled ? 'Past dates cannot be selected' : undefined}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

type ThemedTimePickerProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
};

// Wheel geometry: every row is TIME_ROW_HEIGHT tall and each column shows TIME_VISIBLE_ROWS
// rows, with half the remainder as padding so the first/last items can reach the centre.
// A single selection band sits across both wheels at that centre, so they always line up.
const TIME_ROW_HEIGHT = 36;
const TIME_VISIBLE_ROWS = 5;
const TIME_WHEEL_PADDING = ((TIME_VISIBLE_ROWS - 1) / 2) * TIME_ROW_HEIGHT;
const TIME_HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));
const TIME_MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));
const TIME_QUICK_MINUTES = ['00', '15', '30', '45'];

function parseTime24(value: string): { hour: string; minute: string; meridiem: 'AM' | 'PM' } {
  const [rawHour = '12', rawMinute = '00'] = (value || '').split(':');
  const parsedHour = Number(rawHour);
  const safeHour = Number.isNaN(parsedHour) ? 12 : parsedHour;
  const minuteNumber = Number(rawMinute);
  return {
    hour: String(safeHour % 12 || 12).padStart(2, '0'),
    minute: String(Number.isNaN(minuteNumber) ? 0 : Math.min(59, Math.max(0, minuteNumber))).padStart(2, '0'),
    meridiem: safeHour >= 12 ? 'PM' : 'AM',
  };
}

function toTime24(hour: string, minute: string, meridiem: 'AM' | 'PM'): string {
  const base = Number(hour) % 12;
  return `${String(meridiem === 'PM' ? base + 12 : base).padStart(2, '0')}:${minute}`;
}

function formatTime12(value: string): string {
  if (!value) return '';
  const { hour, minute, meridiem } = parseTime24(value);
  return `${Number(hour)}:${minute} ${meridiem}`;
}

function TimeWheel({
  items,
  selected,
  onSelect,
  ariaLabel,
}: {
  items: string[];
  selected: string;
  onSelect: (value: string) => void;
  ariaLabel: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const settleTimer = useRef<number | undefined>(undefined);
  const selectedIndex = Math.max(0, items.indexOf(selected));

  // Keep the selected item on the centre band (scrollTop on the column only - unlike
  // scrollIntoView this never scrolls the page).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const target = selectedIndex * TIME_ROW_HEIGHT;
    if (Math.abs(el.scrollTop - target) > 1) el.scrollTo({ top: target, behavior: 'smooth' });
  }, [selectedIndex]);

  useLayoutEffect(() => {
    if (ref.current) ref.current.scrollTop = selectedIndex * TIME_ROW_HEIGHT;
    // Initial position only; later changes animate via the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  // Wheel/drag/touch scrolling: once the column settles, pick whatever is on the band.
  const handleScroll = () => {
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const index = Math.min(items.length - 1, Math.max(0, Math.round(el.scrollTop / TIME_ROW_HEIGHT)));
      if (items[index] !== selected) onSelect(items[index]);
    }, 120);
  };

  return (
    <div
      ref={ref}
      className="time-wheel"
      role="listbox"
      aria-label={ariaLabel}
      onScroll={handleScroll}
      style={{ height: TIME_ROW_HEIGHT * TIME_VISIBLE_ROWS, paddingBlock: TIME_WHEEL_PADDING }}
    >
      {items.map((item) => (
        <button
          key={item}
          type="button"
          role="option"
          aria-selected={item === selected}
          className={`time-wheel-item ${item === selected ? 'selected' : ''}`}
          style={{ height: TIME_ROW_HEIGHT }}
          onClick={() => onSelect(item)}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

function ThemedTimePicker({ label, value, onChange, required = false }: ThemedTimePickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { hour, minute, meridiem } = parseTime24(value);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  // Changes apply immediately (same as before); value stays 24h "HH:MM".
  const update = (next: Partial<{ hour: string; minute: string; meridiem: 'AM' | 'PM' }>) => {
    onChange(toTime24(next.hour ?? hour, next.minute ?? minute, next.meridiem ?? meridiem));
  };

  return (
    <div className="games-date-filter" ref={containerRef}>
      <span className="games-date-label">
        {label}
        {required && <span style={{ color: Colors.error }}> *</span>}
      </span>
      <button
        type="button"
        className="games-date-trigger"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={`Select ${label.toLowerCase()} time`}
        aria-expanded={isOpen}
      >
        <span>{value ? formatTime12(value) : 'Select time'}</span>
        <Clock size={14} />
      </button>

      {isOpen && (
        <div className="time-picker-popover" role="dialog" aria-label={`${label} picker`}>
          <div className="time-picker-head">
            <div>
              <div className="time-picker-eyebrow">Select time</div>
              <div className="time-picker-readout">
                {Number(hour)}:{minute}
                <span>{meridiem}</span>
              </div>
            </div>
            <button type="button" className="analytics-date-nav" onClick={() => setIsOpen(false)} aria-label="Close time picker">
              <X size={14} />
            </button>
          </div>

          <div className="time-picker-body">
            <div className="time-picker-wheels">
              <div className="time-picker-band" style={{ top: TIME_WHEEL_PADDING, height: TIME_ROW_HEIGHT }} aria-hidden="true" />
              <TimeWheel items={TIME_HOURS} selected={hour} onSelect={(h) => update({ hour: h })} ariaLabel="Hour" />
              <span className="time-picker-colon" aria-hidden="true">:</span>
              <TimeWheel items={TIME_MINUTES} selected={minute} onSelect={(m) => update({ minute: m })} ariaLabel="Minute" />
            </div>

            <div className="time-picker-meridiem" role="radiogroup" aria-label="AM or PM">
              {(['AM', 'PM'] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  role="radio"
                  aria-checked={meridiem === item}
                  className={meridiem === item ? 'active' : ''}
                  onClick={() => update({ meridiem: item })}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="time-picker-quick">
            {TIME_QUICK_MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                className={minute === m ? 'active' : ''}
                onClick={() => update({ minute: m })}
              >
                :{m}
              </button>
            ))}
          </div>

          <div className="time-picker-foot">
            <button
              type="button"
              className="time-picker-clear"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
            >
              Clear
            </button>
            <button type="button" className="time-picker-done" onClick={() => setIsOpen(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function GamesPanel() {
  // Which of the three Game Management cards is showing. All three keep their state and
  // data-fetching hooks regardless of which is active (they're plain state on this component,
  // not separate mounted components), so switching tabs never re-triggers a fetch or loses
  // pagination/search state the way unmounting a child component would.
  const [activeGamesTab, setActiveGamesTab] = useState<'add' | 'scheduled' | 'record'>('add');

  // Create Game State
  const [homeTeam, setHomeTeam] = useState('');
  const [awayTeam, setAwayTeam] = useState('');
  const [homeTeamId, setHomeTeamId] = useState('');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [gameDate, setGameDate] = useState('');
  const [gameTime, setGameTime] = useState('');
  const [venue, setVenue] = useState('');
  const [network, setNetwork] = useState('');
  const [seasonTag, setSeasonTag] = useState('');
  const [homeAudioUrl, setHomeAudioUrl] = useState('');
  const [awayAudioUrl, setAwayAudioUrl] = useState('');
  const [homeTeamCommentator, setHomeTeamCommentator] = useState('');
  const [awayTeamCommentator, setAwayTeamCommentator] = useState('');
  const [homeTeamSchoolName, setHomeTeamSchoolName] = useState('');
  const [awayTeamSchoolName, setAwayTeamSchoolName] = useState('');
  const [homeTeamThemeColor, setHomeTeamThemeColor] = useState('');
  const [awayTeamThemeColor, setAwayTeamThemeColor] = useState('');
  const [botId, setBotId] = useState('');
  const [gameType, setGameType] = useState<'basketball' | 'football'>('basketball');
  const [seasonOptions, setSeasonOptions] = useState(ACTIVE_SEASON_OPTIONS);
  const [teamOptions, setTeamOptions] = useState<TeamRecord[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [venueOptions, setVenueOptions] = useState<VenueBotMapping[]>([]);
  const [createResponse, setCreateResponse] = useState<ApiResponse<GameResponse> | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  // Tracks the last bot_id we auto-filled from the venue mapping, per form, so a manual
  // override survives even if the venue lookup fires again (e.g. re-render/effect re-run).
  const lastAutoFilledCreateBotId = useRef<string | null>(null);
  const lastAutoFilledEditBotId = useRef<string | null>(null);

  // Games Record grid state (server-paginated, server-searched - shows every game regardless of status)
  const [games, setGames] = useState<Game[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [gridPage, setGridPage] = useState(1);
  const [gridPageSize, setGridPageSize] = useState(20);
  const [gridTotalCount, setGridTotalCount] = useState(0);
  const [gridTotalPages, setGridTotalPages] = useState(1);
  const [gridSearch, setGridSearch] = useState('');
  const [gridSearchInput, setGridSearchInput] = useState('');
  const [gridStatusFilter, setGridStatusFilter] = useState<'' | 'scheduled' | 'unscheduled'>('');
  const [gridHidePast, setGridHidePast] = useState(false);
  // AG Grid column filter model, serialized; sent to the backend so column filters apply
  // across every page instead of just the 20 rows currently loaded.
  const [gridFilterModelJson, setGridFilterModelJson] = useState('{}');
  // Fields that must be filled in before a Games Record row can be saved/published, keyed by
  // game_id -> set of field names currently missing, so only the offending row/cells light up.
  const [invalidGridCells, setInvalidGridCells] = useState<Record<string, Set<string>>>({});
  // AG Grid stops full-row editing on its own (native focus-out) the instant Save is
  // clicked, before our validation even runs - so on a failed validation we need this to
  // explicitly re-open the row for editing again, rather than leaving it looking "closed".
  const gridApiRef = useRef<GridApi<Game> | null>(null);
  // Set right before calling cancelRowEdit - onRowEditingStopped fires for Cancel too (it's a
  // "row editing session over" event, not a "saved" one), and we don't want a Cancel click to
  // get blocked/reopened just because the row's pre-existing data happens to be invalid.
  const isCancellingGridRowRef = useRef(false);

  // Scheduled Games card view state (server-paginated per bucket tab)
  const [scheduledGames, setScheduledGames] = useState<Game[]>([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduledError, setScheduledError] = useState<string | null>(null);
  const [scheduledPage, setScheduledPage] = useState(1);
  const [scheduledPageSize] = useState(20);
  const [scheduledTotalCount, setScheduledTotalCount] = useState(0);
  const [scheduledTotalPages, setScheduledTotalPages] = useState(1);
  const [bucketCounts, setBucketCounts] = useState({ upcoming: 0, today: 0, past: 0 });
  const [activeGameTab, setActiveGameTab] = useState<'upcoming' | 'past' | 'today'>('upcoming');
  const [gameTypeFilter, setGameTypeFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [scheduledSearch, setScheduledSearch] = useState('');
  const [scheduledSearchInput, setScheduledSearchInput] = useState('');
  // Scheduled game cards start collapsed (Commentator/Schedule/Kickoff/Location/Broadcast
  // hidden) - tracks which game_ids have been expanded to show that detail grid.
  const [expandedGameCards, setExpandedGameCards] = useState<Set<string>>(new Set());

  const toggleGameCardDetails = (gameId: string) => {
    setExpandedGameCards((prev) => {
      const next = new Set(prev);
      if (next.has(gameId)) {
        next.delete(gameId);
      } else {
        next.add(gameId);
      }
      return next;
    });
  };

  // Publish / Revoke State
  const [publishLoading, setPublishLoading] = useState<string | null>(null);
  const [revokeLoading, setRevokeLoading] = useState<string | null>(null);
  const [gridDeleteLoading, setGridDeleteLoading] = useState<string | null>(null);

  // Edit State
  const [editingGame, setEditingGame] = useState<Game | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  // Recent Requests State (Logs)
  const [logsGameName, setLogsGameName] = useState('');
  const [logsLimit, setLogsLimit] = useState('10');
  const [recentRequests, setRecentRequests] = useState<RecentRequest[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  // Recent Game Time State
  const [recentGameTimeName, setRecentGameTimeName] = useState('');
  const [recentGameTimeResponse, setRecentGameTimeResponse] = useState<ApiResponse | null>(null);
  const [recentGameTimeLoading, setRecentGameTimeLoading] = useState(false);
  const resolveTeamDetails = useCallback(
    (teamName: string) => {
      const team = teamOptions.find((item) => item.name === teamName);
      if (!team) {
        return {
          commentator: '',
          venue: '',
          schoolName: '',
          themeColor: '',
          basketballLogoUrl: '',
          footballLogoLeftUrl: '',
          footballLogoRightUrl: '',
        };
      }

      const commentator =
        gameType === 'basketball'
          ? team.basketball_commentator || ''
          : team.commentator || '';
      const venue =
        gameType === 'basketball'
          ? team.basketball_venue || ''
          : team.venue || '';

      return {
        commentator,
        venue,
        schoolName: team.school_name || '',
        themeColor: team.theme_color || '',
        basketballLogoUrl: team.basketball_logo_url || '',
        footballLogoLeftUrl: team.football_logo_left_url || '',
        footballLogoRightUrl: team.football_logo_right_url || '',
      };
    },
    [teamOptions, gameType]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSeasonOptions();
  }, []);

  useEffect(() => {
    const loadVenueOptions = async () => {
      const response = await venueBotMappingApi.listVenueBotMappings();
      if (response.success && response.data) {
        setVenueOptions(response.data.venues || []);
      }
    };

    loadVenueOptions();
  }, []);

  const venueBotIdLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    venueOptions.forEach((v) => lookup.set(normalizeVenueKey(v.venue), v.bot_id));
    return lookup;
  }, [venueOptions]);

  useEffect(() => {
    const matchedBotId = venueBotIdLookup.get(normalizeVenueKey(venue));
    if (!matchedBotId) return;
    if (botId === '' || botId === lastAutoFilledCreateBotId.current) {
      setBotId(matchedBotId);
      lastAutoFilledCreateBotId.current = matchedBotId;
    }
  }, [venue, venueBotIdLookup]);

  useEffect(() => {
    if (!editingGame) return;
    const matchedBotId = venueBotIdLookup.get(normalizeVenueKey(editingGame.venue));
    if (!matchedBotId) return;
    const currentBotId = editingGame.bot_id ?? '';
    if (currentBotId === '' || currentBotId === lastAutoFilledEditBotId.current) {
      if (currentBotId !== matchedBotId) {
        lastAutoFilledEditBotId.current = matchedBotId;
        setEditingGame({ ...editingGame, bot_id: matchedBotId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingGame?.venue, venueBotIdLookup]);

  useEffect(() => {
    const loadTeamsForGameType = async () => {
      setTeamsLoading(true);
      const response = await teamApi.listTeams({ game_type: gameType });
      if (response.success && response.data) {
        setTeamOptions(response.data.teams || []);
      } else {
        setTeamOptions([]);
      }
      setTeamsLoading(false);
    };

    loadTeamsForGameType();
    setHomeTeam('');
    setAwayTeam('');
    setHomeTeamId('');
    setAwayTeamId('');
    setHomeAudioUrl('');
    setAwayAudioUrl('');
    setHomeTeamCommentator('');
    setAwayTeamCommentator('');
    setHomeTeamSchoolName('');
    setAwayTeamSchoolName('');
    setHomeTeamThemeColor('');
    setAwayTeamThemeColor('');
  }, [gameType]);

  useEffect(() => {
    const matchedHomeTeam = teamOptions.find((team) => team.name === homeTeam);

    if (matchedHomeTeam) {
      setHomeAudioUrl(matchedHomeTeam.audio_url || '');
      setHomeTeamId(matchedHomeTeam.team_id || '');
      const homeDetails = resolveTeamDetails(homeTeam);
      setHomeTeamCommentator(homeDetails.commentator);
      setHomeTeamSchoolName(homeDetails.schoolName);
      setHomeTeamThemeColor(homeDetails.themeColor);
      if (homeDetails.venue) {
        setVenue(homeDetails.venue);
      }
    } else {
      setHomeAudioUrl('');
      setHomeTeamId('');
      setHomeTeamCommentator('');
      setHomeTeamSchoolName('');
      setHomeTeamThemeColor('');
    }
  }, [homeTeam, resolveTeamDetails, teamOptions]);

  useEffect(() => {
    const matchedAwayTeam = teamOptions.find((team) => team.name === awayTeam);
    if (matchedAwayTeam) {
      setAwayAudioUrl(matchedAwayTeam.audio_url || '');
      setAwayTeamId(matchedAwayTeam.team_id || '');
      const awayDetails = resolveTeamDetails(awayTeam);
      setAwayTeamCommentator(awayDetails.commentator);
      setAwayTeamSchoolName(awayDetails.schoolName);
      setAwayTeamThemeColor(awayDetails.themeColor);
    } else {
      setAwayAudioUrl('');
      setAwayTeamId('');
      setAwayTeamCommentator('');
      setAwayTeamSchoolName('');
      setAwayTeamThemeColor('');
    }
  }, [awayTeam, resolveTeamDetails, teamOptions]);

  const loadSeasonOptions = async () => {
    const response = await seasonApi.listSeasons('active');
    if (response.success && response.data) {
      const nextOptions = response.data.seasons.map((season) => ({
        label: season.season_tag,
        value: season.season_tag,
      }));
      setSeasonOptions(nextOptions.length > 0 ? nextOptions : ACTIVE_SEASON_OPTIONS);
      setSeasonTag((current) =>
        current && nextOptions.some((season) => season.value === current)
          ? current
          : nextOptions[0]?.value || ACTIVE_SEASON_OPTIONS[0]?.value || ''
      );
    } else {
      setSeasonOptions(ACTIVE_SEASON_OPTIONS);
      setSeasonTag((current) =>
        current && ACTIVE_SEASON_OPTIONS.some((season) => season.value === current)
          ? current
          : ACTIVE_SEASON_OPTIONS[0]?.value ?? ''
      );
    }
  };

  // Games Record grid: server-side pagination, search, column filters and ordering.
  // sort='schedule' puts today's games first, then upcoming, then past at the bottom.
  const loadGames = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    const response = await gamesApi.listGames({
      search: gridSearch || undefined,
      status: gridStatusFilter || undefined,
      page: gridPage,
      page_size: gridPageSize,
      sort: 'schedule',
      exclude_past: gridHidePast,
      filter_model: JSON.parse(gridFilterModelJson),
    });
    if (response.success && response.data) {
      setGames(response.data.games || []);
      setGridTotalCount(response.data.count || 0);
      setGridTotalPages(response.data.total_pages || 1);
    } else {
      setListError(response.error || 'Failed to load games');
    }
    setListLoading(false);
  }, [gridSearch, gridStatusFilter, gridPage, gridPageSize, gridHidePast, gridFilterModelJson]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  // Reset to page 1 whenever the search query or any filter changes so results aren't
  // left on a now-out-of-range page.
  useEffect(() => {
    setGridPage(1);
  }, [gridSearch, gridStatusFilter, gridHidePast, gridFilterModelJson]);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timeout = setTimeout(() => setGridSearch(gridSearchInput), 300);
    return () => clearTimeout(timeout);
  }, [gridSearchInput]);

  // Scheduled Games card view: one bucket (upcoming/today/past) server-paginated at a time.
  const loadScheduledGames = useCallback(async () => {
    setScheduledLoading(true);
    setScheduledError(null);
    const response = await gamesApi.listGames({
      status: 'scheduled',
      bucket: activeGameTab,
      game_type: gameTypeFilter || undefined,
      team_name: schoolFilter || undefined,
      search: scheduledSearch || undefined,
      page: scheduledPage,
      page_size: scheduledPageSize,
    });
    if (response.success && response.data) {
      setScheduledGames(response.data.games || []);
      setScheduledTotalCount(response.data.count || 0);
      setScheduledTotalPages(response.data.total_pages || 1);
    } else {
      setScheduledError(response.error || 'Failed to load games');
    }
    setScheduledLoading(false);
  }, [activeGameTab, gameTypeFilter, schoolFilter, scheduledSearch, scheduledPage, scheduledPageSize]);

  useEffect(() => {
    loadScheduledGames();
  }, [loadScheduledGames]);

  // Reset to page 1 whenever the tab or filters change so results aren't left on a
  // now-out-of-range page from a different bucket/filter combination.
  useEffect(() => {
    setScheduledPage(1);
  }, [activeGameTab, gameTypeFilter, schoolFilter, scheduledSearch]);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timeout = setTimeout(() => setScheduledSearch(scheduledSearchInput), 300);
    return () => clearTimeout(timeout);
  }, [scheduledSearchInput]);

  const loadBucketCounts = useCallback(async () => {
    const response = await gamesApi.getScheduledGameBucketCounts({
      game_type: gameTypeFilter || undefined,
      team_name: schoolFilter || undefined,
    });
    if (response.success && response.data) {
      setBucketCounts({
        upcoming: response.data.upcoming,
        today: response.data.today,
        past: response.data.past,
      });
    }
  }, [gameTypeFilter, schoolFilter]);

  useEffect(() => {
    loadBucketCounts();
  }, [loadBucketCounts]);

  // Refresh both games views (grid + scheduled cards) plus the tab counts - used after any
  // mutation (create/update/publish/revoke/delete) so every consumer of game data stays in sync.
  const refreshAllGameViews = useCallback(() => {
    loadGames();
    loadScheduledGames();
    loadBucketCounts();
  }, [loadGames, loadScheduledGames, loadBucketCounts]);

  const gridDefaultColDef = useMemo<ColDef<Game>>(
    () => ({
      editable: true,
      filter: true,
    }),
    []
  );

  const GRID_EDITABLE_FIELDS = useMemo<Array<keyof Game>>(
    () => [
      'game_type',
      'home_team_commentator',
      'away_team_commentator',
      'home_audio_url',
      'away_audio_url',
      'date',
      'time',
      'venue',
      'bot_id',
      'network',
    ],
    []
  );

  const rowEditing = useDataTableRowEditing<Game, (typeof GRID_EDITABLE_FIELDS)[number]>({
    editableFields: GRID_EDITABLE_FIELDS,
    firstEditableField: 'home_team_commentator',
    getRowId: (game) => game.game_id,
    onSave: async (gameId, changes, fullRow) => {
      // validateRowOnStop already ran for this exact stop via the onRowEditingStopped
      // wrapper below (which fires unconditionally, unlike this onSave callback - see its
      // comment for why that gap mattered). Re-running it here is what actually gates the
      // API call: if it's invalid, bail out before persisting anything.
      if (!validateRowOnStop(gameId, fullRow)) return;

      if (Object.keys(changes).length === 0) return;
      const response = await gamesApi.updateGame(gameId, changes as GameUpdate);
      if (response.success) {
        toast.success('Game updated');
      } else {
        toast.error(response.error || 'Failed to update game');
      }
      refreshAllGameViews();
    },
  });

  const handlePublishGame = useCallback(
    async (gameId: string) => {
      setPublishLoading(gameId);
      try {
        const response = await gamesApi.publishGame(gameId);
        if (response.success) {
          toast.success('Game published');
          refreshAllGameViews();
        } else {
          toast.error(normalizeToastError(response.error || 'Failed to publish game'));
        }
      } finally {
        setPublishLoading(null);
      }
    },
    [refreshAllGameViews]
  );

  const handleRevokeGame = useCallback(
    async (gameId: string) => {
      const confirmRevoke = window.confirm('Are you sure you want to revoke this game?');
      if (!confirmRevoke) return;

      setRevokeLoading(gameId);
      try {
        const response = await gamesApi.revokeGame(gameId);
        if (response.success) {
          toast.success('Game revoked');
          refreshAllGameViews();
          if (editingGame?.game_id === gameId) {
            setEditingGame(null);
          }
        } else {
          toast.error(response.error || 'Failed to revoke game');
        }
      } finally {
        setRevokeLoading(null);
      }
    },
    [editingGame, refreshAllGameViews]
  );

  const handleDeleteGameRow = useCallback(
    async (gameId: string) => {
      const confirmDelete = window.confirm('Are you sure you want to delete this game?');
      if (!confirmDelete) return;

      setGridDeleteLoading(gameId);
      try {
        const response = await gamesApi.deleteGame(gameId);
        if (response.success) {
          toast.success('Game deleted');
          refreshAllGameViews();
          if (editingGame?.game_id === gameId) {
            setEditingGame(null);
          }
        } else {
          toast.error(response.error || 'Failed to delete game');
        }
      } finally {
        setGridDeleteLoading(null);
      }
    },
    [editingGame, refreshAllGameViews]
  );

  // Red border on a required cell once its row has failed Save/Publish validation - cleared
  // as soon as that row's validation passes again (see Save/Publish/Cancel/Edit below).
  const invalidCellStyle = useCallback(
    (field: string) =>
      (params: { data?: Game }) =>
        // Always return an explicit style object (never undefined) - some AG Grid versions
        // only clear a cell's previously-applied inline style when cellStyle returns a real
        // replacement value, not when it returns undefined, which left the red border
        // visible even after the row had actually been saved successfully.
        params.data && invalidGridCells[params.data.game_id]?.has(field)
          ? { border: `2px solid ${Colors.error}` }
          : { border: '2px solid transparent' },
    [invalidGridCells]
  );

  const clearGridRowInvalid = useCallback((gameId: string) => {
    setInvalidGridCells((prev) => {
      if (!(gameId in prev)) return prev;
      const next = { ...prev };
      delete next[gameId];
      return next;
    });
  }, []);

  // Runs on EVERY row-editing-stopped event (wired via the onRowEditingStopped wrapper on
  // DataTable below, which AG Grid fires unconditionally) as well as from onSave (which only
  // fires when useDataTableRowEditing detects an actual value diff since editing started).
  // That diff-gating is exactly the gap that let an unchanged, still-invalid row silently
  // "succeed" on a second Save click with nothing retyped - onRowEditingStopped doesn't
  // care whether anything changed, so it always re-validates.
  //
  // Deliberately does NOT try to re-open the row for editing on failure (an earlier version
  // did): calling startEditingCell from here raced with AG Grid's own teardown of the just-
  // stopped session and could re-trigger another immediate stop, which re-ran this same
  // validation, which reopened again, etc. - a feedback loop that stacked the same toast over
  // and over. Simpler and stable instead: show one error (deduped by a stable toast id, so
  // even if this does fire more than once it updates the same toast rather than piling up),
  // mark the red border, and leave the row closed - the user clicks Edit again to retry.
  const validateRowOnStop = useCallback((gameId: string, rowData: Game): boolean => {
    const missing = getMissingGridFields(rowData);
    const hasPastDate = !missing.has('date') && isPastDateKey(convertUTCDateStringToEST(rowData.date, rowData.time));
    const invalidAudioFields = [
      { field: 'home_audio_url', label: 'Home Audio URL', value: rowData.home_audio_url ?? rowData.audio_url },
      { field: 'away_audio_url', label: 'Away Audio URL', value: rowData.away_audio_url },
    ].filter(({ value }) => isInvalidAudioUrl(value));

    if (missing.size > 0 || hasPastDate) {
      const nextMissing = new Set(missing);
      if (hasPastDate) nextMissing.add('date');
      invalidAudioFields.forEach(({ field }) => nextMissing.add(field));
      setInvalidGridCells((prev) => ({ ...prev, [gameId]: nextMissing }));
      gridApiRef.current?.refreshCells({ force: true });
      const labels = REQUIRED_GRID_FIELDS.filter(({ field }) => missing.has(field) || (field === 'date' && hasPastDate)).map(({ label }) => label);
      toast.error(`Fill in ${labels.join(', ')} before saving this game.`, { id: `grid-row-invalid-${gameId}` });
      return false;
    }
    if (invalidAudioFields.length > 0) {
      setInvalidGridCells((prev) => ({ ...prev, [gameId]: new Set(invalidAudioFields.map(({ field }) => field)) }));
      gridApiRef.current?.refreshCells({ force: true });
      toast.error(`${invalidAudioFields.map(({ label }) => label).join(', ')}: ${INVALID_AUDIO_URL_MESSAGE}.`, { id: `grid-row-invalid-${gameId}` });
      return false;
    }
    clearGridRowInvalid(gameId);
    gridApiRef.current?.refreshCells({ force: true });
    return true;
  }, [clearGridRowInvalid]);

  // Keeps an invalid row genuinely re-opened for editing (not just showing Save/Cancel -
  // the cells themselves need to be live editors again so the user can actually fix them).
  // Driven by React's own effect scheduling rather than calling startEditingCell straight out
  // of an AG Grid event callback: that earlier approach raced with the grid's still-in-
  // progress teardown of the just-stopped session and could immediately re-trigger another
  // stop, which re-validated, which tried to reopen again, etc. - a feedback loop. An effect
  // only re-runs when invalidGridCells or editingRowId actually change, which naturally
  // breaks that loop: once the reopen succeeds, editingRowId updates to match and the effect
  // has nothing left to do; if it doesn't succeed, neither dependency changes again on its
  // own, so this doesn't retry in a tight cycle either.
  useEffect(() => {
    const gameIdToReopen = Object.keys(invalidGridCells).find((id) => id !== rowEditing.editingRowId);
    if (!gameIdToReopen) return;

    const api = gridApiRef.current;
    const rowNode = api?.getRowNode(gameIdToReopen);
    if (api && rowNode && rowNode.rowIndex !== null && rowNode.rowIndex !== undefined) {
      api.startEditingCell({ rowIndex: rowNode.rowIndex, colKey: 'home_team_commentator' });
    }
  }, [invalidGridCells, rowEditing.editingRowId]);

  const getTeamByGameSide = useCallback(
    (game: Game, side: 'home' | 'away') => {
      const teamId = side === 'home' ? game.home_team_id : game.away_team_id;
      const teamName = side === 'home' ? game.home_team : game.away_team;
      return teamOptions.find((team) => team.team_id === teamId || team.name === teamName);
    },
    [teamOptions]
  );

  const getPublishValidationErrors = useCallback(
    (game: Game) => {
      const errors: string[] = [];

      if (!game.date?.trim()) {
        errors.push('Date is required.');
      }
      if (!game.time?.trim()) {
        errors.push('Time is required.');
      }
      if (!game.venue?.trim()) {
        errors.push('Venue is required.');
      }
      if (!game.bot_id?.trim()) {
        errors.push('Bot ID is required.');
      }

      const homeTeamRecord = getTeamByGameSide(game, 'home');
      const awayTeamRecord = getTeamByGameSide(game, 'away');

      if (homeTeamRecord?.status === 'active') {
        const homeCommentator = (game.home_team_commentator ?? '').trim();
        const homeAudio = (game.home_audio_url ?? game.audio_url ?? '').trim();
        if (!homeCommentator || !homeAudio) {
          errors.push('Home team is active: home commentator and home audio URL are required.');
        }
      }

      if (awayTeamRecord?.status === 'active') {
        const awayCommentator = (game.away_team_commentator ?? '').trim();
        const awayAudio = (game.away_audio_url ?? '').trim();
        if (!awayCommentator || !awayAudio) {
          errors.push('Away team is active: away commentator and away audio URL are required.');
        }
      }

      return errors;
    },
    [getTeamByGameSide]
  );

  const gridColumnDefs = useMemo<ColDef<Game>[]>(
    () => [
      {
        field: 'game_type',
        headerName: 'Game Type',
        width: 130,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: ['basketball', 'football'] },
      },
      {
        field: 'game_name',
        headerName: 'Game Name',
        width: 240,
        editable: false,
        cellRenderer: (params: ICellRendererParams<Game>) => {
          const name = params.value || '';
          if (!name) return '-';
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name}
              </span>
              <CopyButton text={name} toastMessage="Game name copied" size={13} />
            </div>
          );
        },
      },

      {
        field: 'date',
        headerName: 'Match Date',
        width: 130,
        cellEditor: DateCellEditor,
        cellStyle: invalidCellStyle('date'),
        // Date filter (evaluated server-side on the Eastern date shown here). Limited to the
        // options the backend implements; range is inclusive on both ends.
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
          inRangeInclusive: true,
        },
        valueGetter: (params: ValueGetterParams<Game>) =>
          params.data ? convertUTCDateStringToEST(params.data.date, params.data.time) : '',
        valueSetter: (params: ValueSetterParams<Game>) => {
          if (!params.data) return false;
          const estTime = convertUTCTimeStringToEST(params.data.time, params.data.date);
          params.data.date = convertESTDateStringToUTC(params.newValue, estTime);
          return true;
        },
      },
      {
        field: 'time',
        headerName: 'Time',
        width: 100,
        cellStyle: invalidCellStyle('time'),
        cellEditor: TimeCellEditor,
        valueGetter: (params: ValueGetterParams<Game>) =>
          params.data ? convertUTCTimeStringToEST(params.data.time, params.data.date) : '',
        valueSetter: (params: ValueSetterParams<Game>) => {
          if (!params.data) return false;
          const estDate = convertUTCDateStringToEST(params.data.date, params.data.time);
          params.data.time = convertESTTimeStringToUTC(params.newValue, estDate);
          return true;
        },
      },
      { field: 'venue', headerName: 'Venue', width: 160, cellStyle: invalidCellStyle('venue') },
      { field: 'bot_id', headerName: 'Bot ID', width: 120, cellStyle: invalidCellStyle('bot_id') },
      {
        field: 'home_team_commentator',
        headerName: 'Home Team Commentator',
        width: 180,
        cellStyle: invalidCellStyle('home_team_commentator'),
      },
      {
        field: 'away_team_commentator',
        headerName: 'Away Team Commentator',
        width: 180,
        cellStyle: invalidCellStyle('away_team_commentator'),
      },
      {
        field: 'home_audio_url',
        headerName: 'Home Audio URL',
        width: 220,
        cellStyle: invalidCellStyle('home_audio_url'),
      },
      {
        field: 'away_audio_url',
        headerName: 'Away Audio URL',
        width: 220,
        cellStyle: invalidCellStyle('away_audio_url'),
      },
      { field: 'home_team', headerName: 'Home Team', width: 140, editable: false },
      { field: 'away_team', headerName: 'Away Team', width: 140, editable: false },
      { field: 'network', headerName: 'Network', width: 130 },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        editable: false,
        valueGetter: (params: ValueGetterParams<Game>) =>
          params.data
            ? STATUS_OPTIONS.find((option) => option.value === params.data!.status)?.label ?? params.data.status
            : '',
      },
      {
        headerName: 'Actions',
        colId: 'actions',
        editable: false,
        filter: false,
        pinned: 'right',
        minWidth: 250,
        // Prevents AG Grid's own double-click-to-edit handling from firing on this cell — in
        // editType="fullRow" mode, double-clicking ANY cell (even a non-editable one) would
        // otherwise still start full-row editing as long as some other column IS editable.
        cellRendererParams: {
          suppressMouseEventHandling: () => true,
        },
        cellRenderer: (params: ICellRendererParams<Game>) => {
          // Also shows Save/Cancel whenever this row currently has a validation error,
          // regardless of AG Grid's own isRowEditing - otherwise there's a visible gap
          // between the moment AG Grid closes the row (its native focus-out behavior) and
          // the reopen effect below actually succeeding, where the button briefly (or, if
          // the reopen for whatever reason doesn't land, permanently) shows "Edit" while an
          // error is still up.
          const hasInvalidCells = Boolean(params.data && invalidGridCells[params.data.game_id]?.size);
          const isEditing = rowEditing.isRowEditing(params.data) || hasInvalidCells;
          return (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', height: '100%' }}>
              {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Validation happens exclusively in useDataTableRowEditing's onSave,
                      // which AG Grid calls once the row's data is fully flushed - checking
                      // here too (on params.data, before that flush) raced with it and could
                      // read a stale value for whichever cell was still focused, sometimes
                      // reporting a field as still-empty right after it had just been fixed.
                      rowEditing.saveRowEdit(params);
                    }}
                    onDoubleClick={(e) => e.stopPropagation()}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: Colors.success,
                      color: '#FFFFFF',
                    }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      isCancellingGridRowRef.current = true;
                      rowEditing.cancelRowEdit(params);
                      if (params.data) {
                        clearGridRowInvalid(params.data.game_id);
                        params.api.refreshCells({ force: true });
                      }
                    }}
                    onDoubleClick={(e) => e.stopPropagation()}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '6px',
                      border: `1px solid ${withAlpha(Colors.error, 0.4)}`,
                      cursor: 'pointer',
                      backgroundColor: '#FFFFFF',
                      color: Colors.error,
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled={params.data?.status === 'scheduled'}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (params.data) {
                      clearGridRowInvalid(params.data.game_id);
                      params.api.refreshCells({ force: true });
                    }
                    rowEditing.startRowEdit(params);
                  }}
                  onDoubleClick={(e) => e.stopPropagation()}
                  title={params.data?.status === 'scheduled' ? 'Published games cannot be edited - revoke first' : undefined}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: '6px',
                    border: 'none',
                    cursor: params.data?.status === 'scheduled' ? 'not-allowed' : 'pointer',
                    background: params.data?.status === 'scheduled' ? '#F1F3F9' : `linear-gradient(145deg, #1e377c, ${Colors.secondary})`,
                    color: params.data?.status === 'scheduled' ? '#A0AEC0' : '#FFFFFF',
                    opacity: params.data?.status === 'scheduled' ? 0.7 : 1,
                  }}
                >
                  Edit
                </button>
              )}
              <button
                type="button"
                disabled={params.data?.status === 'scheduled' || publishLoading === params.data?.game_id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!params.data) return;
                  const publishErrors = getPublishValidationErrors(params.data);
                  if (publishErrors.length > 0) {
                    toast.error(`Publish blocked: ${publishErrors.join(' ')}`, {
                      id: `publish-validation-${params.data.game_id}`,
                    });
                    return;
                  }
                  handlePublishGame(params.data.game_id);
                }}
                onDoubleClick={(e) => e.stopPropagation()}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '6px',
                  border: `1px solid ${withAlpha(Colors.border, 0.2)}`,
                  cursor: params.data?.status === 'scheduled' || publishLoading === params.data?.game_id ? 'not-allowed' : 'pointer',
                  backgroundColor: params.data?.status === 'scheduled' || publishLoading === params.data?.game_id ? '#F1F3F9' : '#FFFFFF',
                  color: params.data?.status === 'scheduled' || publishLoading === params.data?.game_id ? '#A0AEC0' : Colors.text,
                  opacity: params.data?.status === 'scheduled' || publishLoading === params.data?.game_id ? 0.7 : 1,
                }}
              >
                Publish
              </button>
              <button
                type="button"
                disabled={params.data?.status !== 'scheduled' || revokeLoading === params.data?.game_id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (params.data) handleRevokeGame(params.data.game_id);
                }}
                onDoubleClick={(e) => e.stopPropagation()}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '6px',
                  border: `1px solid ${withAlpha(Colors.error, 0.4)}`,
                  cursor: params.data?.status !== 'scheduled' || revokeLoading === params.data?.game_id ? 'not-allowed' : 'pointer',
                  backgroundColor: '#FFFFFF',
                  color: Colors.error,
                  opacity: params.data?.status !== 'scheduled' || revokeLoading === params.data?.game_id ? 0.6 : 1,
                }}
              >
                Revoke
              </button>
              <button
                type="button"
                disabled={gridDeleteLoading === params.data?.game_id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (params.data) handleDeleteGameRow(params.data.game_id);
                }}
                onDoubleClick={(e) => e.stopPropagation()}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '6px',
                  border: `1px solid ${withAlpha(Colors.error, 0.4)}`,
                  cursor: gridDeleteLoading === params.data?.game_id ? 'not-allowed' : 'pointer',
                  backgroundColor: '#FFFFFF',
                  color: Colors.error,
                  opacity: gridDeleteLoading === params.data?.game_id ? 0.6 : 1,
                }}
              >
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [
      rowEditing,
      handlePublishGame,
      publishLoading,
      handleRevokeGame,
      revokeLoading,
      handleDeleteGameRow,
      gridDeleteLoading,
      invalidCellStyle,
      clearGridRowInvalid,
      invalidGridCells,
      getPublishValidationErrors,
    ]
  );

  const availableHomeTeams = teamOptions;
  const availableAwayTeams = teamOptions.filter((team) => team.name !== homeTeam);
  const teamOptionLabel = (team: TeamRecord) => `${team.name} (${team.abbreviation}) - ${team.status}`;

  const resolvedHomeCommentator = (homeTeamCommentator.trim() || resolveTeamDetails(homeTeam).commentator).trim();
  const resolvedAwayCommentator = (awayTeamCommentator.trim() || resolveTeamDetails(awayTeam).commentator).trim();
  const hasCreateCommentator = !!(resolvedHomeCommentator || resolvedAwayCommentator);
  const hasCreateAudioUrl = !!(homeAudioUrl.trim() || awayAudioUrl.trim());
  const isHomeAudioUrlInvalid = isInvalidAudioUrl(homeAudioUrl);
  const isAwayAudioUrlInvalid = isInvalidAudioUrl(awayAudioUrl);
  const isCreateFormValid = useMemo(
    () =>
      !!homeTeam &&
      !!awayTeam &&
      homeTeam !== awayTeam &&
      !!gameDate &&
      !!venue.trim() &&
      !!botId.trim() &&
      !!network.trim() &&
      hasCreateCommentator &&
      hasCreateAudioUrl &&
      !isHomeAudioUrlInvalid &&
      !isAwayAudioUrlInvalid,
    [
      homeTeam,
      awayTeam,
      gameDate,
      venue,
      botId,
      network,
      hasCreateCommentator,
      hasCreateAudioUrl,
      isHomeAudioUrlInvalid,
      isAwayAudioUrlInvalid,
    ]
  );

  const isEditFormValid = useMemo(() => {
    if (!editingGame) return false;
    return (
      !!editingGame.date &&
      !!editingGame.time &&
      !!editingGame.venue?.trim() &&
      !!editingGame.bot_id?.trim() &&
      !isInvalidAudioUrl(editingGame.home_audio_url ?? editingGame.audio_url) &&
      !isInvalidAudioUrl(editingGame.away_audio_url)
    );
  }, [editingGame]);

  const handleCreateGame = async () => {
    if (!homeTeam || !awayTeam) {
      toast.error('Home team and away team are required.');
      return;
    }
    if (homeTeam === awayTeam) {
      toast.error('Home team and away team must be different.');
      return;
    }
    if (!gameDate) {
      toast.error('Date is required.');
      return;
    }
    if (!venue.trim()) {
      toast.error('Venue is required.');
      return;
    }
    if (!botId.trim()) {
      toast.error('Bot ID is required.');
      return;
    }
    if (!network.trim()) {
      toast.error('Network is required.');
      return;
    }
    if (!hasCreateCommentator) {
      toast.error('Add at least one home or away commentator.');
      return;
    }
    if (!hasCreateAudioUrl) {
      toast.error('Add at least one home or away audio URL.');
      return;
    }
    if (isHomeAudioUrlInvalid || isAwayAudioUrlInvalid) {
      toast.error(`${isHomeAudioUrlInvalid ? 'Home' : 'Away'} Audio URL: ${INVALID_AUDIO_URL_MESSAGE}.`);
      return;
    }

    setCreateLoading(true);
    const response = await gamesApi.createGame({
      home_team: homeTeam,
      home_team_id: homeTeamId || undefined,
      away_team: awayTeam,
      away_team_id: awayTeamId || undefined,
      date: convertESTDateStringToUTC(gameDate, gameTime),
      time: convertESTTimeStringToUTC(gameTime, gameDate),
      timezone: 'UTC',
      venue: venue,
      network: network,
      season_tag: seasonTag || undefined,
      home_team_commentator: homeTeamCommentator || resolveTeamDetails(homeTeam).commentator,
      away_team_commentator: awayTeamCommentator || resolveTeamDetails(awayTeam).commentator,
      home_team_school_name: homeTeamSchoolName || resolveTeamDetails(homeTeam).schoolName,
      away_team_school_name: awayTeamSchoolName || resolveTeamDetails(awayTeam).schoolName,
      home_team_theme_color: homeTeamThemeColor || resolveTeamDetails(homeTeam).themeColor,
      away_team_theme_color: awayTeamThemeColor || resolveTeamDetails(awayTeam).themeColor,
      home_audio_url: homeAudioUrl || undefined,
      away_audio_url: awayAudioUrl || undefined,
      bot_id: botId.trim(),
      game_type: gameType,
      ...(gameType === 'basketball'
        ? {
            home_basketball_logo_url: resolveTeamDetails(homeTeam).basketballLogoUrl || undefined,
            away_basketball_logo_url: resolveTeamDetails(awayTeam).basketballLogoUrl || undefined,
          }
        : {
            home_football_logo_left_url: resolveTeamDetails(homeTeam).footballLogoLeftUrl || undefined,
            home_football_logo_right_url: resolveTeamDetails(homeTeam).footballLogoRightUrl || undefined,
            away_football_logo_left_url: resolveTeamDetails(awayTeam).footballLogoLeftUrl || undefined,
            away_football_logo_right_url: resolveTeamDetails(awayTeam).footballLogoRightUrl || undefined,
          }),
    });
    setCreateResponse(response);
    setCreateLoading(false);

    if (response.success) {
      refreshAllGameViews();
      // Clear form
      setHomeTeam('');
      setAwayTeam('');
      setHomeTeamId('');
      setAwayTeamId('');
      setGameDate('');
      setGameTime('');
      setVenue('');
      setNetwork('');
      setSeasonTag(seasonOptions[0]?.value ?? ACTIVE_SEASON_OPTIONS[0]?.value ?? '');
      setHomeAudioUrl('');
      setAwayAudioUrl('');
      setHomeTeamCommentator('');
      setAwayTeamCommentator('');
      setHomeTeamSchoolName('');
      setAwayTeamSchoolName('');
      setHomeTeamThemeColor('');
      setAwayTeamThemeColor('');
      setBotId('');
    }
  };

  const handleUpdateGame = async () => {
    if (!editingGame) return;

    if (!editingGame.date) {
      toast.error('Date is required.');
      return;
    }
    if (!editingGame.time) {
      toast.error('Time is required.');
      return;
    }
    if (!editingGame.venue?.trim()) {
      toast.error('Venue is required.');
      return;
    }
    if (!editingGame.bot_id?.trim()) {
      toast.error('Bot ID is required.');
      return;
    }
    if (isInvalidAudioUrl(editingGame.home_audio_url ?? editingGame.audio_url)) {
      toast.error(`Home Audio URL: ${INVALID_AUDIO_URL_MESSAGE}.`);
      return;
    }
    if (isInvalidAudioUrl(editingGame.away_audio_url)) {
      toast.error(`Away Audio URL: ${INVALID_AUDIO_URL_MESSAGE}.`);
      return;
    }

    setEditLoading(true);
    try {
      // home_team/away_team are intentionally omitted — teams can't be changed once a game is created.
      const response = await gamesApi.updateScheduledGame(editingGame.game_id, {
        date: convertESTDateStringToUTC(editingGame.date, editingGame.time),
        time: convertESTTimeStringToUTC(editingGame.time, editingGame.date),
        venue: editingGame.venue,
        network: editingGame.network,
        season_tag: editingGame.season_tag,
        home_team_commentator: editingGame.home_team_commentator ?? '',
        away_team_commentator: editingGame.away_team_commentator ?? '',
        home_team_school_name: editingGame.home_team_school_name,
        away_team_school_name: editingGame.away_team_school_name,
        home_team_theme_color: editingGame.home_team_theme_color,
        away_team_theme_color: editingGame.away_team_theme_color,
        audio_url: editingGame.home_audio_url ?? editingGame.audio_url ?? '',
        home_audio_url: editingGame.home_audio_url ?? editingGame.audio_url ?? '',
        away_audio_url: editingGame.away_audio_url ?? '',
        bot_id: editingGame.bot_id,
        game_type: editingGame.game_type as 'basketball' | 'football',
      });

      if (response.success) {
        setEditingGame(null);
        refreshAllGameViews();
      } else {
        toast.error(response.error || 'Failed to update scheduled game');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleGetRecentRequests = async () => {
    setLogsLoading(true);
    setLogsError(null);
    const response = await imageMatchingApi.getRecentRequests(
      logsGameName || undefined,
      parseInt(logsLimit) || 10
    );
    if (response.success && response.data) {
      setRecentRequests(response.data.requests || []);
    } else {
      setLogsError(response.error || 'Failed to load logs');
      setRecentRequests([]);
    }
    setLogsLoading(false);
  };

  const handleGetRecentGameTime = async () => {
    if (!recentGameTimeName) return;
    setRecentGameTimeLoading(true);
    const response = await imageMatchingApi.getRecentGameTime(recentGameTimeName);
    setRecentGameTimeResponse(response);
    setRecentGameTimeLoading(false);
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const renderTeamOptions = (teams: TeamRecord[]) =>
    teams.map((team) => (
      <option key={team.team_id} value={team.name}>
        {teamOptionLabel(team)}
      </option>
    ));
    
  return (
    <div className="section">
      <h2 className="section-title">
        <Gamepad2 size={24} />
        Game Management
      </h2>

      <div className="teams-editor-tabs">
        <button
          type="button"
          className={`teams-editor-tab ${activeGamesTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveGamesTab('add')}
        >
          Add Game Record
        </button>
        <button
          type="button"
          className={`teams-editor-tab ${activeGamesTab === 'record' ? 'active' : ''}`}
          onClick={() => setActiveGamesTab('record')}
        >
          Games Records
        </button>
        <button
          type="button"
          className={`teams-editor-tab ${activeGamesTab === 'scheduled' ? 'active' : ''}`}
          onClick={() => setActiveGamesTab('scheduled')}
        >
          Scheduled Games
        </button>
      </div>

      {activeGamesTab === 'add' && (
        /* Create Game Card */
        <Card
          title="Add New Game Record"
          description="Add a game record to the system. Once added, you can edit, publish or delete the game as needed."
          headerAction={
            <Button variant="outline" className="btn-small" onClick={() => setImportModalOpen(true)}>
              <Upload size={14} />
              Import from Excel
            </Button>
          }
        >
          {/* Two sections side by side; fields inside use the Manual Operation inline style
              (right-aligned label + compact field), see .game-form-inline in index.css. */}
          <div className="game-form-columns">
          <div className="game-form-column">
          <div className="game-form-section-header">
            <div className="game-form-section-icon game-form-section-icon--matchup">
              <MicStage size={16} />
            </div>
            <div>
              <div className="game-form-section-title">Matchup & Broadcast</div>
              <div className="game-form-section-subtitle">Teams, commentators, and how the game is broadcast.</div>
            </div>
          </div>

          <div className="game-form-section-body game-form-section-body--matchup game-form-inline">
              <div className="form-group">
                <label className="form-label">Game Type</label>
                <select
                  className="form-input"
                  value={gameType}
                  onChange={(e) => setGameType(e.target.value as 'basketball' | 'football')}
                >
                  <option value="basketball">basketball</option>
                  <option value="football">football</option>
                </select>
              </div>
              <Input
                label="Network"
                value={network}
                onChange={setNetwork}
                placeholder="e.g., CW Network"
                required
              />
              <div className="game-form-subgroup game-form-subgroup--home">
                <span className="game-form-subgroup-label">Home</span>
              </div>
              <div className="form-group"  >
                <label className="form-label">
                  Home Team
                  <span style={{ color: Colors.error }}> *</span>
                </label>
                <select
                  className="form-input"
                  value={homeTeam}
                  onChange={(e) => setHomeTeam(e.target.value)}
                  disabled={teamsLoading}
                >
                  <option value="">
                    {teamsLoading ? 'Loading teams...' : 'Select home team'}
                  </option>
                  {renderTeamOptions(availableHomeTeams)}
                </select>
              </div>
              <Input
                label="Home Commentator"
                value={homeTeamCommentator}
                onChange={setHomeTeamCommentator}
                placeholder="John Doe, Jane Smith"
                info="To display multiple commentators, list their names separated by commas."
                required
              />
              <Input
                label="Home Audio URL"
                type="url"
                value={homeAudioUrl}
                onChange={setHomeAudioUrl}
                placeholder="https://stream.example.com/audio.m3u8"
                info="At least one of Home Audio URL or Away Audio URL is required."
                error={isHomeAudioUrlInvalid ? INVALID_AUDIO_URL_MESSAGE : undefined}
              />
              <div className="game-form-subgroup game-form-subgroup--away">
                <span className="game-form-subgroup-label">Away</span>
              </div>
              <div className="form-group"  >
                <label className="form-label">
                  Away Team
                  <span style={{ color: Colors.error }}> *</span>
                </label>
                <select
                  className="form-input"
                  value={awayTeam}
                  onChange={(e) => setAwayTeam(e.target.value)}
                  disabled={teamsLoading}
                >
                  <option value="">
                    {teamsLoading ? 'Loading teams...' : 'Select away team'}
                  </option>
                  {renderTeamOptions(availableAwayTeams)}
                </select>
              </div>
              <Input
                label="Away Commentator"
                value={awayTeamCommentator}
                onChange={setAwayTeamCommentator}
                placeholder="John Doe, Jane Smith"
                info="To display multiple commentators, list their names separated by commas."
                required
              />
              <Input
                label="Away Audio URL"
                type="url"
                value={awayAudioUrl}
                onChange={setAwayAudioUrl}
                placeholder="https://stream.example.com/audio.m3u8"
                info="At least one of Home Audio URL or Away Audio URL is required."
                error={isAwayAudioUrlInvalid ? INVALID_AUDIO_URL_MESSAGE : undefined}
              />
          </div>

          </div>

          <div className="game-form-column">
          <div className="game-form-section-header">
            <div className="game-form-section-icon game-form-section-icon--info">
              <MapPin size={16} />
            </div>
            <div>
              <div className="game-form-section-title">Game Info & Location</div>
              <div className="game-form-section-subtitle">When and where the game happens.</div>
            </div>
          </div>

          <div className="game-form-section-body game-form-section-body--info game-form-inline">
            <div className="form-grid-2">
              <ThemedDatePicker
                label="Date"
                value={gameDate}
                onChange={setGameDate}
                compact
                required
              />
              <ThemedTimePicker
                label="Time (EST)"
                value={gameTime}
                onChange={setGameTime}
              />
            </div>

            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">
                  Venue
                  <span style={{ color: Colors.error }}> *</span>
                </label>
                <select
                  className="form-input"
                  value={isKnownVenue(venue, venueOptions) ? venue : ''}
                  onChange={(e) => {
                    if (e.target.value) setVenue(e.target.value);
                  }}
                >
                  <option value="">Pick a known venue…</option>
                  {venueOptions.map((v) => (
                    <option key={v.venue} value={v.venue}>
                      {v.venue}
                    </option>
                  ))}
                </select>
                <input
                  className="form-input"
                  type="text"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  placeholder="Or type a venue name"
                  style={{ marginTop: '8px' }}
                />
              </div>
              <Input
                label="Bot ID"
                value={botId}
                onChange={setBotId}
                required
                info="Auto-filled from the venue's bot mapping; edit to override for this game."
                placeholder="e.g., bot-12345"
              />
            </div>
          </div>
          </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <Button
            onClick={handleCreateGame}
            loading={createLoading}
            disabled={!isCreateFormValid}
            variant="success"
            className='btn btn-small btn-success '
          >
            <Plus size={18} />
            Add Game
          </Button>
          </div>

          {createResponse && (
            <ResponseDisplay
              response={createResponse.success ? createResponse.data : createResponse.error}
              error={!createResponse.success}
              label={createResponse.success ? 'Game Created' : 'Error'}
              collapsible
            />
          )}
        </Card>
      )}

      <GameImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={refreshAllGameViews}
      />

      {activeGamesTab === 'scheduled' && (
        /* Games List Card */
        <Card
          title="Scheduled Games"
          description="All published games in the system"
          headerAction={
            <Button
              onClick={() => {
                loadScheduledGames();
                loadBucketCounts();
              }}
              loading={scheduledLoading}
              className="btn btn-small btn-primary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              <RefreshCw size={14} />
              Refresh
              </Button>
            }
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '16px',
            padding: '12px 14px',
            borderRadius: '14px',
            background: '#F7F9FF',
            border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
               <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: '#5F719A', textTransform: 'uppercase' }}>
                  Game Type
                </span>
                <select
                  className="form-input"
                  value={gameTypeFilter}
                  onChange={(e) => setGameTypeFilter(e.target.value)}
                  style={{ minWidth: '125px' }}
                >
                  <option value="">All</option>
                  <option value="basketball">basketball</option>
                  <option value="football">football</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: '#5F719A', textTransform: 'uppercase' }}>
                  School
                </span>
                <select
                  className="form-input"
                  value={schoolFilter}
                  onChange={(e) => setSchoolFilter(e.target.value)}
                  style={{ minWidth: '200px' }}
                >
                  <option value="">All</option>
                  {renderTeamOptions(availableHomeTeams)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: '#5F719A', textTransform: 'uppercase' }}>
                  Search
                </span>
                <input
                  className="form-input"
                  type="text"
                  value={scheduledSearchInput}
                  onChange={(e) => setScheduledSearchInput(e.target.value)}
                  placeholder="Search by game name..."
                  style={{ minWidth: '220px' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: 'transparent', textTransform: 'uppercase' }}>
                  {' '}
                </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 14px',
                    borderRadius: '999px',
                    backgroundColor: withAlpha(Colors.accent, 0.12),
                    border: `1px solid ${withAlpha(Colors.accent, 0.3)}`,
                    color: Colors.text,
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                >
                  <span>{scheduledTotalCount} {scheduledTotalCount === 1 ? 'record' : 'records'}</span>
                  {(gameTypeFilter || schoolFilter || scheduledSearchInput) && (
                  <button
                    type="button"
                    onClick={() => {
                      setGameTypeFilter('');
                      setSchoolFilter('');
                      setScheduledSearchInput('');
                    }}
                    title="Clear filters"
                    aria-label="Clear game type, school, and search filters"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      background: 'transparent',
                      color: Colors.text,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <X size={12} />
                  </button>
                  )}
                </div>
              </div>
            </div>
              </div>
              <div style={{
                display: 'flex',
                gap: '4px',
                padding: '4px',
                backgroundColor: '#FFFFFF',
                borderRadius: '999px',
                border: `1px solid ${withAlpha(Colors.border, 0.2)}`,
                flexWrap: 'wrap',
              }}>
                <button
                  type="button"
                  onClick={() => setActiveGameTab('upcoming')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: activeGameTab === 'upcoming' ? '#FFFFFF' : Colors.secondary,
                    backgroundColor: activeGameTab === 'upcoming' ? Colors.secondary : 'transparent',
                  }}
                >
                  Upcoming ({bucketCounts.upcoming})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveGameTab('today')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: activeGameTab === 'today' ? '#FFFFFF' : Colors.secondary,
                    backgroundColor: activeGameTab === 'today' ? Colors.secondary : 'transparent',
                  }}
                >
                  Today ({bucketCounts.today})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveGameTab('past')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: activeGameTab === 'past' ? '#FFFFFF' : Colors.secondary,
                    backgroundColor: activeGameTab === 'past' ? Colors.secondary : 'transparent',
                  }}
                >
                  Past ({bucketCounts.past})
                </button>
              </div>
            </div>

            
          </div>

          {scheduledError && (
            <div className="alert alert-warning">
              <span>{scheduledError}</span>
            </div>
          )}

          {scheduledGames.length === 0 && !scheduledLoading && !scheduledError && (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#5F719A',
              backgroundColor: '#F7F9FF',
              borderRadius: '12px',
              border: `1px solid ${withAlpha(Colors.border, 0.12)}`,
            }}>
              <Gamepad2 size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
              <p>No games scheduled</p>
            </div>
          )}

          {scheduledGames.length > 0 && (
            <div className="scheduled-games-glass-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '14px', maxHeight: '600px', overflowY: 'auto', alignItems: 'start' }}>
              {scheduledGames.map((game, index) => {
                const isExpanded = expandedGameCards.has(game.game_id);
                return (
                <div
                  key={game.game_id}
                  className="game-list-card game-list-card--glass"
                  style={{ padding: '8px' }}
                >
                  {/* Game Header */}
                  <div className="game-card-header" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <div style={{ width: '100%' }}>
                      <div
                        className="game-matchup-banner"
                        style={{
                          position: 'relative',
                          display: 'flex',
                          minHeight: '125px',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          marginBottom: '4px',
                          // Team colours feed the glass background in .game-matchup-banner.
                          ['--home-color' as string]: game.home_team_theme_color || Colors.text,
                          ['--away-color' as string]: game.away_team_theme_color || '#B99C4E',
                        } as React.CSSProperties}
                      >
                        <div
                          className="game-matchup-date"
                          style={{
                            position: 'absolute',
                            top: '10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            padding: '5px 16px',
                            borderRadius: '999px',
                            whiteSpace: 'nowrap',
                            zIndex: 2,
                          }}
                        >
                          {activeGameTab === 'today' ? "Today's Game" : formatDate(game.date)}
                        </div>

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '32px 12px 16px' }}>
                          <TeamLogoBadge
                            logoUrl={
                              game.game_type === 'basketball'
                                ? game.home_basketball_logo_url
                                : game.home_football_logo_right_url
                            }
                            teamName={game.home_team}
                            themeColor={game.home_team_theme_color}
                          />
                          <span style={{ color: '#FFFFFF', fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', textShadow: '0 2px 4px rgba(0,0,0,0.35)' }}>
                            {game.home_team}
                          </span>
                        </div>

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '32px 12px 16px' }}>
                          <TeamLogoBadge
                            logoUrl={
                              game.game_type === 'basketball'
                                ? game.away_basketball_logo_url
                                : game.away_football_logo_left_url
                            }
                            teamName={game.away_team}
                            themeColor={game.away_team_theme_color}
                          />
                          <span style={{ color: '#FFFFFF', fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', textShadow: '0 2px 4px rgba(0,0,0,0.35)' }}>
                            {game.away_team}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', paddingInline: '5px' }}>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: Colors.text }}>
                        {game.home_team} vs. {game.away_team}
                      </div>
                      <div className="game-card-actions">
                        <Button
                          onClick={() => {
                            lastAutoFilledEditBotId.current = game.bot_id ?? null;
                            setEditingGame({
                              ...game,
                              date: convertUTCDateStringToEST(game.date, game.time),
                              time: convertUTCTimeStringToEST(game.time, game.date),
                            });
                          }}
                          variant="secondary"
                          className="game-icon-button"
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          onClick={() => handleRevokeGame(game.game_id)}
                          loading={revokeLoading === game.game_id}
                          variant="danger"
                          size="small"
                          title="Revoke"
                          aria-label="Revoke"
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>

                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '5px 12px',
                      backgroundColor: '#F7F9FF',
                      borderRadius: '999px',
                      border: `1px solid ${withAlpha(Colors.border, 0.14)}`,
                      width: 'fit-content',
                    }}>
                      <span style={{
                        fontSize: '12px',
                        color: Colors.text,
                        fontFamily: 'monospace',
                        fontWeight: 700,
                      }}>
                        {game.game_name}
                      </span>
                      <CopyButton text={game.game_name} toastMessage="Game name copied" size={12} />
                    </div>
                  </div>

                  {/* Game Type & Bot ID Badges */}
                  <div style={{ marginTop: '10px',marginInline: '2px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 600,
                      backgroundColor: game.game_type === 'basketball' ? 'rgba(255, 149, 0, 0.15)' : withAlpha(Colors.success, 0.15),
                      color: game.game_type === 'basketball' ? '#FF9500' : Colors.success,
                    }}>
                      {game.game_type}
                    </span>
                    {game.bot_id && (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 600,
                        backgroundColor: 'rgba(88, 86, 214, 0.15)',
                        color: '#5856D6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}>
                        <Bot size={11} />
                        {game.bot_id}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleGameCardDetails(game.game_id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto',
                        padding: '6px 14px', borderRadius: '999px', border: 'none', cursor: 'pointer',
                        backgroundColor: withAlpha(Colors.secondary, 0.12), color: Colors.secondary,
                        fontSize: '12px', fontWeight: 700,
                      }}
                    >
                      {isExpanded ? 'Hide details' : 'Show details'}
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>

                  {/* Game Details */}
                  {isExpanded && (
                  <div className="game-detail-grid" style={{ marginTop: '10px' }}>
                     <div className='gameDetailSection' style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#5F719A' }}>
                        <span className='upperCaseLabel' >Commentrator</span>
                        <span>Home: {game.home_team_commentator || '-'}</span>
                        <span>Away: {game.away_team_commentator || '-'}</span>
                      </div>
                    <div className='gameDetailSection' style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#5F719A' }}>
                      <span className='upperCaseLabel'>Schedule</span>
                      <Calendar size={14} />
                      {formatDate(convertUTCDateStringToEST(game.date, game.time))}
                    </div>
                    <div className='gameDetailSection' style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#5F719A' }}>
                       <span className='upperCaseLabel'>Kickoff</span>
                      <Clock size={14} />
                      {formatTime(game.time, game.date)}
                    </div>
                    <div className='gameDetailSection' style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#5F719A' }}>
                      <span className='upperCaseLabel'>Location</span>
                      <MapPin size={14} />
                      {game.venue}
                    </div>
                    <div className='gameDetailSection' style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#5F719A' }}>
                      <span className='upperCaseLabel'>Broadcast Channel</span>
                      <Tv size={14} />
                      {game.network}
                    </div>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {scheduledTotalPages > 1 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              marginTop: '16px',
            }}>
              <button
                type="button"
                className="btn btn-small btn-secondary"
                disabled={scheduledPage <= 1 || scheduledLoading}
                onClick={() => setScheduledPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={14} />
                Prev
              </button>
              <span style={{ fontSize: '12px', color: '#5F719A', fontWeight: 600 }}>
                Page {scheduledPage} of {scheduledTotalPages}
              </span>
              <button
                type="button"
                className="btn btn-small btn-secondary"
                disabled={scheduledPage >= scheduledTotalPages || scheduledLoading}
                onClick={() => setScheduledPage((p) => Math.min(scheduledTotalPages, p + 1))}
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </Card>
      )}

      {activeGamesTab === 'record' && (
        <Card
          title="Games Record"
          description="View and edit all unpublished games in the system"
          headerAction={
            <Button
              onClick={loadGames}
              loading={listLoading}
              className="btn btn-small btn-primary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              <RefreshCw size={14} />
              Refresh
            </Button>
          }
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            marginBottom: '12px',
            flexWrap: 'wrap',
          }}>
            <div style={{ minWidth: '220px', flex: '0 1 260px' }}>
              <input
                className="form-input"
                type="text"
                value={gridSearchInput}
                onChange={(e) => setGridSearchInput(e.target.value)}
                placeholder="Search all games..."
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              {([
                { value: '', label: 'All records' },
                { value: 'scheduled', label: 'Published' },
                { value: 'unscheduled', label: 'Unpublished' },
              ] as const).map((opt) => (
                <label key={opt.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#5F719A', fontWeight: 600, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={gridStatusFilter === opt.value}
                    onChange={() => setGridStatusFilter(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#5F719A', fontWeight: 600, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={gridHidePast}
                onChange={(e) => setGridHidePast(e.target.checked)}
              />
              Hide past games
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
              <span style={{ fontSize: '12px', color: '#5F719A', fontWeight: 600 }}>Rows per page</span>
              <select
                className="form-input"
                value={gridPageSize}
                onChange={(e) => {
                  setGridPageSize(Number(e.target.value));
                  setGridPage(1);
                }}
                style={{ minWidth: '80px' }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>

          {listError && (
            <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
              <span>{listError}</span>
            </div>
          )}

          <div style={{ width: '100%', overflowX: 'auto' }}>
            <div style={{ minWidth: '100px' }}>
              <DataTable<Game>
                rowData={games}
                columnDefs={gridColumnDefs}
                defaultColDef={gridDefaultColDef}
                getRowId={(data) => data.game_id}
                editType="fullRow"
                domLayout="normal"
                height="520px"
                enableSearch={false}
                pagination={false}
                gridProps={{
                  ...rowEditing.gridEventHandlers,
                  suppressMovableColumns: true,
                  onGridReady: (event: GridReadyEvent<Game>) => {
                    gridApiRef.current = event.api;
                  },
                  // Column filters run on the server (across all pages); the grid itself
                  // must not filter the current page again. Must be a stable reference - AG Grid
                  // fires filterChanged whenever this prop changes, which stops any row edit in progress.
                  alwaysPassFilter: gridAlwaysPassFilter,
                  onFilterChanged: (event: FilterChangedEvent<Game>) => {
                    const next = JSON.stringify(event.api.getFilterModel() || {});
                    setGridFilterModelJson((current) => (current === next ? current : next));
                  },
                  // Wraps (not replaces) useDataTableRowEditing's own handler, which only
                  // tracks editingRowId. AG Grid fires this on every stop regardless of
                  // whether any value actually changed - unlike onSave, so this is what
                  // catches "clicked Save again without fixing the invalid field".
                  onRowEditingStopped: (event: RowEditingStoppedEvent<Game>) => {
                    rowEditing.gridEventHandlers.onRowEditingStopped?.(event);
                    if (isCancellingGridRowRef.current) {
                      isCancellingGridRowRef.current = false;
                      return;
                    }
                    if (event.data) validateRowOnStop(event.data.game_id, event.data);
                  },
                }}
              />
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '12px',
          }}>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={gridPage <= 1 || listLoading}
              onClick={() => setGridPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={14} />
              Prev
            </button>
            <span style={{ fontSize: '12px', color: '#5F719A', fontWeight: 600 }}>
              Page {gridPage} of {gridTotalPages} · {gridTotalCount} total
            </span>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={gridPage >= gridTotalPages || listLoading}
              onClick={() => setGridPage((p) => Math.min(gridTotalPages, p + 1))}
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        </Card>
      )}

      {/* Monitoring Section */}
      {/* <h2 className="section-title" style={{ marginTop: '32px' }}>
        <Activity size={24} />
        Game Monitoring
      </h2> */}

      <div className="grid grid-2">
        {/* Recent Requests (Logs) Card */}
        {/* <Card
          title="Recent Sync Requests"
          description="View recent image matching requests and logs"
          headerAction={<Activity size={20} style={{ color: '#5856D6' }} />}
        >
          <div className="form-grid-wide">
            <Input
              label="Game Name (optional)"
              value={logsGameName}
              onChange={setLogsGameName}
              placeholder="Filter by game name"
            />
            <Input
              label="Limit"
              value={logsLimit}
              onChange={setLogsLimit}
              placeholder="10"
              type="number"
            />
          </div>

          <Button
            onClick={handleGetRecentRequests}
            loading={logsLoading}
            className='btn-small'
            variant="secondary"
          >
            <RefreshCw size={18} />
            Fetch Logs
          </Button>

          {logsError && (
            <div className="alert alert-warning" style={{ marginTop: '12px' }}>
              <span>{logsError}</span>
            </div>
          )}

          {recentRequests.length > 0 && (
            <div className="table-scroll-wrap" style={{ marginTop: '16px', maxHeight: '600px', overflowY: 'auto' }}>
              <table className="table" style={{ fontSize: '12px', minWidth: '860px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${withAlpha(Colors.border, 0.14)}` }}>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#5F719A', fontWeight: 700 }}>User</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#5F719A', fontWeight: 700 }}>Processing</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#5F719A', fontWeight: 700 }}>Sync Gap</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#5F719A', fontWeight: 700 }}>Manual Sync Gap</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#5F719A', fontWeight: 700 }}>Clicked Time</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#5F719A', fontWeight: 700 }}>Visual</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#5F719A', fontWeight: 700 }}>User Query</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRequests.map((req) => (
                    <tr key={req.id} style={{ borderBottom: `1px solid ${withAlpha(Colors.border, 0.08)}` }}>
                      <td style={{ padding: '10px 8px', color: Colors.text }}>{req.user_id || '-'}</td>
                      <td style={{ padding: '10px 8px', color: Colors.success }}>
                        {req.processing_time_seconds ? `${req.processing_time_seconds.toFixed(2)}s` : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', color: Colors.accent }}>
                        {req.sync_time_gap_seconds !== undefined ? `${req.sync_time_gap_seconds}s` : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', color: Colors.secondary }}>
                        {req.manual_sync_time_gap !== undefined ? `${req.manual_sync_time_gap}s` : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', color: Colors.text }}>{req.user_clicked_time || '-'}</td>
                      <td style={{ padding: '10px 8px' }}>
                        {req.visualization_url ? (
                          <a
                            href={req.visualization_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: Colors.accent, display: 'flex', alignItems: 'center', gap: '4px' }}
                          >
                            <ExternalLink size={12} />
                            View
                          </a>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        {req.user_query_url ? (
                          <a
                            href={req.user_query_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: Colors.accent, display: 'flex', alignItems: 'center', gap: '4px' }}
                          >
                            <ExternalLink size={12} />
                            View
                          </a>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {recentRequests.length === 0 && !logsLoading && !logsError && (
            <div style={{
              marginTop: '16px',
              textAlign: 'center',
              padding: '24px',
              color: Colors.textLight,
              backgroundColor: '#F7F9FF',
              borderRadius: '8px'
            }}>
              <Activity size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
              <p>No logs yet. Click "Fetch Logs" to load.</p>
            </div>
          )}
        </Card> */}

        {/* Recent Game Time Card */}
        {/* <Card
          title="Last Indexed Game Time"
          description="Check the last game-time that got indexed"
          headerAction={<Timer size={20} style={{ color: '#FF9500' }} />}
        >
          <Input
            label="Game Name"
            value={recentGameTimeName}
            onChange={setRecentGameTimeName}
            placeholder="e.g., wake_forest_vs_duke_20260126"
            required
          />

          <Button
            onClick={handleGetRecentGameTime}
            loading={recentGameTimeLoading}
            disabled={!recentGameTimeName}
            variant="warning"
            className='btn-small'
          >
            <Timer size={18} />
            Get Last Game Time
          </Button>

          {recentGameTimeResponse && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: recentGameTimeResponse.success ? withAlpha(Colors.success, 0.1) : withAlpha(Colors.error, 0.1),
              borderRadius: '8px',
              border: `1px solid ${recentGameTimeResponse.success ? withAlpha(Colors.success, 0.3) : withAlpha(Colors.error, 0.3)}`,
            }}>
              {recentGameTimeResponse.success ? (
                <pre style={{
                  margin: 0,
                  fontSize: '13px',
                  color: Colors.success,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {JSON.stringify(recentGameTimeResponse.data, null, 2)}
                </pre>
              ) : (
                <span style={{ color: Colors.error, fontSize: '13px' }}>
                  {recentGameTimeResponse.error}
                </span>
              )}
            </div>
          )}
        </Card> */}
      </div>

      {/* Edit Game Modal */}
      {editingGame && (
        <div
          className="games-modal-overlay"
          onClick={() => setEditingGame(null)}
        >
          <div
            className="games-modal-panel"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                paddingBottom: '16px',
                borderBottom: `1px solid ${withAlpha(Colors.border, 0.12)}`
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: withAlpha(Colors.accent, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Edit size={20} color={Colors.text} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: Colors.text }}>
                    Edit Game
                  </h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#5F719A', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{editingGame.game_name}</span>
                    <CopyButton text={editingGame.game_name} toastMessage="Game name copied" size={13} />
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEditingGame(null)}
                style={{
                  background: '#F7F9FF',
                  border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={18} color={Colors.text} />
              </button>
            </div>

            {/* Edit Form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block',  }}>Home Team</label>
                  <select
                    className="form-input"
                    value={editingGame.home_team}
                    disabled
                    title="Teams cannot be changed after a game is created"
                  >
                    <option value={editingGame.home_team}>{editingGame.home_team}</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Away Team</label>
                  <select
                    className="form-input"
                    value={editingGame.away_team}
                    disabled
                    title="Teams cannot be changed after a game is created"
                  >
                    <option value={editingGame.away_team}>{editingGame.away_team}</option>
                  </select>
                </div>
              </div>
              <p style={{ fontSize: '12px', color: '#5F719A', margin: 0 }}>
                Teams cannot be changed once a game is created.
              </p>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Home Commentator</label>
                  <input
                    type="text"
                    value={editingGame.home_team_commentator ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, home_team_commentator: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                    placeholder="John Doe, Jane Smith"
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Away Commentator</label>
                  <input
                    type="text"
                    value={editingGame.away_team_commentator ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, away_team_commentator: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                    placeholder="John Doe, Jane Smith"
                    required
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Home School Name</label>
                  <input
                    type="text"
                    value={editingGame.home_team_school_name ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, home_team_school_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                    placeholder="Home school name"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Away School Name</label>
                  <input
                    type="text"
                    value={editingGame.away_team_school_name ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, away_team_school_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                    placeholder="Away school name"
                  />
                </div>
              </div>

              {/* <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Home Theme Color</label>
                  <input
                    type="text"
                    value={editingGame.home_team_theme_color ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, home_team_theme_color: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                    placeholder="#0033A0"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Away Theme Color</label>
                  <input
                    type="text"
                    value={editingGame.away_team_theme_color ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, away_team_theme_color: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                    placeholder="#BA0C2F"
                  />
                </div>
              </div> */}

              <div className="form-grid-2">
                <div>
                  <ThemedDatePicker
                    label="Date"
                    value={editingGame.date}
                    onChange={(value) => setEditingGame({ ...editingGame, date: value })}
                    compact
                    required
                  />
                </div>
                <div>
                  <ThemedTimePicker
                    label="Time"
                    value={editingGame.time}
                    onChange={(value) => setEditingGame({ ...editingGame, time: value })}
                    required
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Venue <span style={{ color: Colors.error }}>*</span></label>
                  <select
                    className="form-input"
                    value={isKnownVenue(editingGame.venue, venueOptions) ? editingGame.venue : ''}
                    onChange={(e) => {
                      if (e.target.value) setEditingGame({ ...editingGame, venue: e.target.value });
                    }}
                  >
                    <option value="">Pick a known venue…</option>
                    {venueOptions.map((v) => (
                      <option key={v.venue} value={v.venue}>
                        {v.venue}
                      </option>
                    ))}
                  </select>
                  <input
                    className="form-input"
                    type="text"
                    value={editingGame.venue}
                    onChange={(e) => setEditingGame({ ...editingGame, venue: e.target.value })}
                    placeholder="Or type a venue name"
                    style={{ marginTop: '8px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>
                    Bot ID <span style={{ color: Colors.error }}>*</span>
                    <span style={{ fontWeight: 400, color: '#8C9BC2' }}> (auto-filled from venue, editable)</span>
                  </label>
                  <input
                    type="text"
                    value={editingGame.bot_id ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, bot_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                    placeholder="e.g., bot-12345"
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Network</label>
                  <input
                    type="text"
                    value={editingGame.network}
                    onChange={(e) => setEditingGame({ ...editingGame, network: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Home Audio URL
                    <span
                      title="At least one of Home Audio URL or Away Audio URL is required."
                      style={{ cursor: 'help', opacity: 0.6, display: 'flex', flexShrink: 0 }}
                    >
                      <Info size={14} />
                    </span>
                  </label>
                  <input
                    type="url"
                    value={editingGame.home_audio_url ?? editingGame.audio_url ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, home_audio_url: e.target.value, audio_url: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: isInvalidAudioUrl(editingGame.home_audio_url ?? editingGame.audio_url) ? `1px solid ${Colors.error}` : `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                    fontSize: '14px',
                    }}
                  />
                  {isInvalidAudioUrl(editingGame.home_audio_url ?? editingGame.audio_url) && (
                    <span style={{ color: Colors.error, fontSize: '12px', marginTop: '4px', display: 'block' }}>{INVALID_AUDIO_URL_MESSAGE}</span>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Away Audio URL
                    <span
                      title="At least one of Home Audio URL or Away Audio URL is required."
                      style={{ cursor: 'help', opacity: 0.6, display: 'flex', flexShrink: 0 }}
                    >
                      <Info size={14} />
                    </span>
                  </label>
                  <input
                    type="url"
                    value={editingGame.away_audio_url ?? ''}
                    onChange={(e) => setEditingGame({ ...editingGame, away_audio_url: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: isInvalidAudioUrl(editingGame.away_audio_url) ? `1px solid ${Colors.error}` : `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                    placeholder="https://stream.example.com/audio.m3u8"
                  />
                  {isInvalidAudioUrl(editingGame.away_audio_url) && (
                    <span style={{ color: Colors.error, fontSize: '12px', marginTop: '4px', display: 'block' }}>{INVALID_AUDIO_URL_MESSAGE}</span>
                  )}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '12px', color: '#5F719A', marginBottom: '4px', display: 'block' }}>Game Type</label>
                  <select
                    value={editingGame.game_type}
                    onChange={(e) => setEditingGame({ ...editingGame, game_type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                      backgroundColor: '#F7F9FF',
                      color: Colors.text,
                      fontSize: '14px',
                    }}
                  >
                    <option value="basketball">basketball</option>
                    <option value="football">football</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              marginTop: '24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              paddingTop: '16px',
              borderTop: `1px solid ${withAlpha(Colors.border, 0.12)}`
            }}>
              <Button
                onClick={() => setEditingGame(null)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateGame}
                loading={editLoading}
                disabled={!isEditFormValid}
                variant="success"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
